import { Heading } from '@mm/components';
import { useSessionStorage } from '@mm/hooks';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import {
  selectCurrentUser,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { CHATBOT_EDITOR } from '../../../../../../common/constants/routes';
import { usePhoneNumber } from '../../../../../../common/hooks/use-phone-number';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { ChannelIcon } from '../../../../liveChat/components/ChannelIcon';
import { useChannelConnection } from '../../Automations/AutomationEdit/InputTriggers/use-channel-connection';
import { useConnectNativeIntegration } from '../../Integrations/NativeIntegrations/use-connect-native-integration';
import {
  EDUCATIONAL_CARD_DEFINITIONS,
  EDUCATIONAL_CARD_TYPES,
  EducationalCardDefinition,
  EducationalCards,
} from './EducationalCardDefinitions';
import styles from './EducationalCardsBlock.module.css';

type Props = {
  card: EducationalCardDefinition;
  onClick: () => void;
  setDimissiedCards: (value: string[]) => void;
  dismissedCards: string[];
};

const EducationalCard = (props: Props) => {
  const { card, onClick, setDimissiedCards, dismissedCards } = props;

  return (
    <div className={styles.card}>
      <img src={card.backgroundImage} className={styles.backgroundImage} />
      {card?.channel && (
        <ChannelIcon channel={card.channel} className={styles.icon} />
      )}
      {card?.icon && <img src={card?.icon} className={styles.icon} />}
      <div className={styles.cardDescriptionBlock}>
        <div className={styles.cardHeadingAndDescription}>
          <Heading size={3} className={styles.cardHeading}>
            {card.heading}
          </Heading>
          <div className={styles.description}>{card.description}</div>
        </div>
        <div className={styles.buttonsWrapper}>
          {card?.dismissable && (
            <PrimaryButton
              className={styles.dismissButton}
              color="transparent-no-shadow"
              onClick={() =>
                setDimissiedCards([...dismissedCards, `${card.cardType}`])
              }
            >
              Dismiss
            </PrimaryButton>
          )}
          <PrimaryButton className={styles.actionButton} onClick={onClick}>
            {card.button}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export const EducationalCardsBlock = () => {
  const {
    isSMSConnected,
    isInstagramConnected,
    isFacebookConnected,
    isEmailConnected,
    connectChannel,
  } = useChannelConnection(() => {});
  const modalActions = useModalActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const activeFacebookPage = useSelector(selectActiveFacebookPage);
  const currentUser = useSelector(selectCurrentUser);
  const { needsToVerifyEmail, userAttributes } = useSelector(
    selectSessionSelector,
  );
  const firstChatbotId = useSelector(
    (state: StoreState) => state.chatbot.basicListByCompany[0]?.id,
  );
  const botId = chatbotId || firstChatbotId;
  const history = useHistory();
  const { isSmsSupported } = usePhoneNumber();
  const { connectNativeIntegration } = useConnectNativeIntegration();

  const sessionStorageKeyName = `educationalCardsBlockHideState-${activeFacebookPage?.id}`;
  const [hiddenEducationalCards, setHideEducationalCards] = useSessionStorage(
    sessionStorageKeyName,
    [],
  );

  const redirect = useCallback(
    (path: string) => {
      if (botId) {
        history.push(path);
      }
    },
    [botId, history],
  );

  const cards: EducationalCardDefinition[] = useMemo(() => {
    const hideConnectedChannels = (cardType: EducationalCards) => {
      switch (cardType) {
        case 'instagram':
          return !isInstagramConnected;
        case 'facebook':
          return !isFacebookConnected;
        case 'sms':
          return isSmsSupported && !isSMSConnected;
        case 'email':
          return !isEmailConnected;
        case 'download_app':
          return !userAttributes?.hasMobile;
        case 'confirm_email':
          return needsToVerifyEmail;
        case 'connect_google_sheets':
          return !currentUser?.googleSheetsActivated;
        case 'connect_mailchimp':
          return !currentUser?.mailchimpActivated;
        default:
          return true;
      }
    };

    const visibleCards = EDUCATIONAL_CARD_TYPES.filter(hideConnectedChannels)
      .filter(cardType => !hiddenEducationalCards.includes(cardType))
      .map(cardType => ({
        ...EDUCATIONAL_CARD_DEFINITIONS[cardType],
        cardType,
      }));

    return visibleCards.sort((a, b) => a.priority - b.priority);
  }, [
    isFacebookConnected,
    isInstagramConnected,
    isSMSConnected,
    isEmailConnected,
    isSmsSupported,
    hiddenEducationalCards,
    needsToVerifyEmail,
    userAttributes,
    currentUser,
  ]);

  const onTriggerClick = useCallback(
    (educatonalCard: EducationalCardDefinition) => {
      switch (educatonalCard.cardType) {
        case 'facebook':
          connectChannel('facebook');
          break;
        case 'instagram':
          connectChannel('instagram');
          break;
        case 'sms':
          connectChannel('sms');
          break;
        case 'email':
          connectChannel('email');
          break;
        case 'create_automation':
          redirect(
            BotBuilderRoutes.automationHome.getLink({
              chatbotId: botId,
            }),
          );
          break;
        case 'visit_inbox':
          redirect(`${CHATBOT_EDITOR}/${botId}/live-chat`);
          break;
        case 'download_app':
          modalActions.openMobileDownloadModal();
          break;
        case 'confirm_email':
          modalActions.openVerifyEmailModal();
          break;
        case 'connect_google_sheets':
          connectNativeIntegration('googleSheets');
          break;
        case 'connect_mailchimp':
          connectNativeIntegration('mailchimp');
          break;
        default:
          break;
      }
    },
    [connectChannel, modalActions, redirect, botId, connectNativeIntegration],
  );

  return (
    <div className={styles.container}>
      <Heading size={2} className={styles.heading}>
        The 20 Minute Sales Outreach Automation Workflow
      </Heading>
      <div className={styles.cardsBlock}>
        {cards.map(
          (card, index) =>
            index < 4 && (
              <EducationalCard
                card={card}
                key={card.heading}
                onClick={() => onTriggerClick(card)}
                setDimissiedCards={setHideEducationalCards}
                dismissedCards={hiddenEducationalCards}
              />
            ),
        )}
      </div>
    </div>
  );
};
